import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  PLATFORM_TEMPLATE_CATEGORIES,
  PlatformTemplateCategory,
} from "../../../helpers/super_admin_platform_templates_helper";
import {
  clonePlatformTemplate,
  CloneAs,
  favoritePlatformTemplate,
  getPlatformTemplates,
  localizePlatformDescription,
  PLATFORM_TEMPLATE_GALLERY_TAGS,
  PlatformTemplate,
  PlatformTemplateTag,
  unfavoritePlatformTemplate,
} from "../../../helpers/platform_templates_helper";

const PER_PAGE = 24;

type CategoryFilter = "" | PlatformTemplateCategory;
type SortMode = "default" | "popular";

/**
 * Galeria read-only dos modelos oficiais + preview + clone (DOC 03 § 5.2 · LAC-024/032).
 */
const PlatformTemplatesGallery = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  document.title = t("platformTemplates.documentTitle");

  const [items, setItems] = useState<PlatformTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [category, setCategory] = useState<CategoryFilter>("");
  const [tag, setTag] = useState<"" | PlatformTemplateTag>("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortMode>("default");
  const [inSeason, setInSeason] = useState(false);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<PlatformTemplate | null>(null);
  const [cloning, setCloning] = useState<PlatformTemplate | null>(null);
  const [cloneBusy, setCloneBusy] = useState(false);
  const [draftBusyId, setDraftBusyId] = useState<number | null>(null);

  // Debounce leve da busca — evita request a cada tecla.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlatformTemplates({
        page,
        per_page: PER_PAGE,
        category: category || undefined,
        search: search || undefined,
        tag: tag || undefined,
        sort,
        in_season: inSeason,
        favorites: favoritesOnly,
      });
      if (!res?.success) {
        setError(t("platformTemplates.error.load"));
        setItems([]);
        return;
      }
      setItems(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (err: any) {
      setError(err?.message || t("platformTemplates.error.load"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, category, search, tag, sort, inSeason, favoritesOnly, t]);

  useEffect(() => {
    load();
  }, [load]);

  const onClone = async (
    as: CloneAs,
    source?: PlatformTemplate | null,
    opts?: { name?: string; subject?: string }
  ) => {
    const guide = source ?? cloning;
    if (!guide) {
      return;
    }
    setCloneBusy(true);
    setError(null);
    try {
      const res = await clonePlatformTemplate(guide.id, {
        as,
        name: opts?.name,
        subject: opts?.subject,
      });
      const id = res?.data?.id;
      const href =
        id && as === "campaign"
          ? `/campaigns/${id}/edit?openTest=1`
          : id
            ? `/templates/${id}/edit`
            : null;

      toast.success(
        <span>
          {res?.message || t("platformTemplates.toast.cloned")}{" "}
          {href ? (
            <Link
              to={href}
              className="text-decoration-underline fw-semibold"
            >
              {t("platformTemplates.toast.openCopy")}
            </Link>
          ) : null}
        </span>,
        { autoClose: 6000 }
      );

      setCloning(null);
      setPreviewing(null);
      if (href) {
        navigate(href);
      }
    } catch (err: any) {
      setError(err?.message || t("platformTemplates.error.clone"));
      setCloning(null);
    } finally {
      setCloneBusy(false);
      setDraftBusyId(null);
    }
  };

  /** One-click: campanha rascunho com nome/assunto sugeridos + abre teste. */
  const onQuickDraftCampaign = async (item: PlatformTemplate) => {
    const draftName = t("platformTemplates.clone.draftName", {
      name: item.name,
    });
    setDraftBusyId(item.id);
    await onClone("campaign", item, {
      name: draftName,
      subject: item.name,
    });
  };

  const helpUrl = helpUrlForRoute("/templates/gallery", i18n.language);
  const hasFilters = Boolean(category || tag || search || inSeason || favoritesOnly);

  const clearFilters = () => {
    setCategory("");
    setTag("");
    setSearchInput("");
    setSearch("");
    setInSeason(false);
    setFavoritesOnly(false);
    setSort("default");
    setPage(1);
  };

  const toggleFavorite = async (item: PlatformTemplate) => {
    try {
      if (item.is_favorite) {
        await unfavoritePlatformTemplate(item.id);
        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id ? { ...row, is_favorite: false } : row
          )
        );
      } else {
        await favoritePlatformTemplate(item.id);
        setItems((prev) =>
          prev.map((row) =>
            row.id === item.id ? { ...row, is_favorite: true } : row
          )
        );
      }
    } catch (err: any) {
      toast.error(err?.message || t("platformTemplates.error.favorite"));
    }
  };

  const thumbnailSrcDoc = useMemo(() => {
    return (html: string) =>
      `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>body{margin:0;background:#fff;}</style></head><body>${html}</body></html>`;
  }, []);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("platformTemplates.title")}
          pageTitle={t("platformTemplates.breadcrumb")}
        />
        <ToastContainer closeButton={false} limit={1} />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card className="mb-3">
          <CardBody className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <div>
              <h5 className="mb-1">{t("platformTemplates.introTitle")}</h5>
              <p className="text-muted mb-0">
                {t("platformTemplates.introDescription")}
              </p>
            </div>
            <Button color="soft-secondary" tag={Link} to="/templates">
              {t("platformTemplates.backToMine")}
            </Button>
          </CardBody>
        </Card>

        <Row className="g-2 mb-3 align-items-center">
          <Col md={5} lg={4}>
            <InputGroup>
              <InputGroupText>
                <i className="ri-search-line" />
              </InputGroupText>
              <Input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t("platformTemplates.search.placeholder")}
                aria-label={t("platformTemplates.search.placeholder")}
              />
            </InputGroup>
          </Col>
          <Col md={7} lg={8} className="d-flex flex-wrap gap-2 justify-content-md-end">
            <ButtonGroup size="sm">
              <Button
                color={sort === "default" ? "primary" : "light"}
                onClick={() => {
                  setPage(1);
                  setSort("default");
                }}
              >
                {t("platformTemplates.sort.default")}
              </Button>
              <Button
                color={sort === "popular" ? "primary" : "light"}
                onClick={() => {
                  setPage(1);
                  setSort("popular");
                }}
              >
                {t("platformTemplates.sort.popular")}
              </Button>
            </ButtonGroup>
            <Button
              size="sm"
              color={inSeason ? "warning" : "light"}
              onClick={() => {
                setPage(1);
                setInSeason((v) => !v);
              }}
            >
              {t("platformTemplates.filter.inSeason")}
            </Button>
            <Button
              size="sm"
              color={favoritesOnly ? "danger" : "light"}
              onClick={() => {
                setPage(1);
                setFavoritesOnly((v) => !v);
              }}
            >
              <i className="ri-heart-fill me-1" />
              {t("platformTemplates.filter.favorites")}
            </Button>
            {hasFilters && (
              <Button size="sm" color="soft-secondary" onClick={clearFilters}>
                {t("platformTemplates.filter.clear")}
              </Button>
            )}
          </Col>
        </Row>

        <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <span className="text-muted small me-1">
            {t("platformTemplates.filter.category")}
          </span>
          <ButtonGroup size="sm">
            <Button
              color={!category ? "primary" : "light"}
              onClick={() => {
                setPage(1);
                setCategory("");
              }}
            >
              {t("platformTemplates.filter.all")}
            </Button>
            {PLATFORM_TEMPLATE_CATEGORIES.map((cat) => (
              <Button
                key={cat}
                color={category === cat ? "primary" : "light"}
                onClick={() => {
                  setPage(1);
                  setCategory(cat);
                }}
              >
                {t(`platformTemplates.category.${cat}`)}
              </Button>
            ))}
          </ButtonGroup>
        </div>

        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <span className="text-muted small me-1">
            {t("platformTemplates.filter.tag")}
          </span>
          <Button
            size="sm"
            color={!tag ? "primary" : "light"}
            onClick={() => {
              setPage(1);
              setTag("");
            }}
          >
            {t("platformTemplates.filter.allTags")}
          </Button>
          {PLATFORM_TEMPLATE_GALLERY_TAGS.map((slug) => (
            <Button
              key={slug}
              size="sm"
              color={tag === slug ? "primary" : "light"}
              onClick={() => {
                setPage(1);
                setTag(slug);
              }}
            >
              {t(`platformTemplates.tag.${slug}`, { defaultValue: slug })}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardBody className="text-center py-5">
              <div className="avatar-lg mx-auto mb-3">
                <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-1">
                  <i className="ri-gallery-line" />
                </div>
              </div>
              <h5 className="mb-2">
                {hasFilters
                  ? t("platformTemplates.empty.filteredTitle")
                  : t("platformTemplates.empty.title")}
              </h5>
              <p className="text-muted mb-3">
                {hasFilters
                  ? t("platformTemplates.empty.filteredDescription")
                  : t("platformTemplates.empty.description")}
              </p>
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                {hasFilters ? (
                  <Button color="light" onClick={clearFilters}>
                    {t("platformTemplates.filter.clear")}
                  </Button>
                ) : (
                  <>
                    <Button color="primary" tag={Link} to="/templates/new">
                      {t("platformTemplates.empty.cta")}
                    </Button>
                    <Button
                      color="soft-secondary"
                      tag="a"
                      href={helpUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("platformTemplates.empty.help")}
                    </Button>
                  </>
                )}
              </div>
            </CardBody>
          </Card>
        ) : (
          <>
            <p className="text-muted mb-3">
              {t("platformTemplates.totalCount", { count: total })}
            </p>
            <Row className="g-3">
              {items.map((item) => {
                const description = localizePlatformDescription(
                  item.description,
                  i18n.language
                );
                const clones = item.clone_count ?? 0;
                return (
                  <Col key={item.id} sm={6} lg={4} xl={3}>
                    <Card className="h-100 shadow-none border platform-template-card">
                      <button
                        type="button"
                        className="border-0 border-bottom overflow-hidden p-0 text-start w-100 position-relative platform-template-thumb"
                        style={{
                          height: 200,
                          cursor: "zoom-in",
                          background:
                            "linear-gradient(180deg, #f8f9fa 0%, #eef1f4 100%)",
                        }}
                        onClick={() => setPreviewing(item)}
                        aria-label={t("platformTemplates.actions.preview")}
                      >
                        {item.thumbnail_url ? (
                          <img
                            src={item.thumbnail_url}
                            alt={item.name}
                            className="w-100 h-100"
                            style={{ objectFit: "cover" }}
                          />
                        ) : item.html_body ? (
                          <div
                            className="mx-auto my-2 bg-white shadow-sm overflow-hidden"
                            style={{
                              width: "88%",
                              height: "86%",
                              borderRadius: 6,
                              border: "1px solid rgba(0,0,0,.06)",
                            }}
                          >
                            <iframe
                              title={item.name}
                              srcDoc={thumbnailSrcDoc(item.html_body)}
                              sandbox=""
                              tabIndex={-1}
                              style={{
                                width: "200%",
                                height: "200%",
                                border: 0,
                                transform: "scale(0.5)",
                                transformOrigin: "top left",
                                pointerEvents: "none",
                              }}
                            />
                          </div>
                        ) : (
                          <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                            <i className="ri-mail-line fs-1" />
                          </div>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm position-absolute top-0 end-0 m-2 btn-light"
                          onClick={(e) => {
                            e.stopPropagation();
                            void toggleFavorite(item);
                          }}
                          aria-label={t("platformTemplates.actions.favorite")}
                        >
                          <i
                            className={
                              item.is_favorite
                                ? "ri-heart-fill text-danger"
                                : "ri-heart-line"
                            }
                          />
                        </button>
                        <span className="position-absolute bottom-0 end-0 m-2 badge bg-dark-subtle text-dark">
                          <i className="ri-zoom-in-line me-1" />
                          {t("platformTemplates.actions.preview")}
                        </span>
                        {item.season_status === "active" && (
                          <span className="position-absolute top-0 start-0 m-2 badge bg-warning text-dark">
                            {t("platformTemplates.season.active")}
                          </span>
                        )}
                        {item.season_status === "upcoming" && (
                          <span className="position-absolute top-0 start-0 m-2 badge bg-info-subtle text-info">
                            {t("platformTemplates.season.upcoming")}
                          </span>
                        )}
                      </button>
                      <CardHeader className="pb-0 border-0">
                        <div className="d-flex align-items-start justify-content-between gap-2">
                          <h5 className="card-title mb-1 text-truncate">
                            {item.name}
                          </h5>
                          <Badge color="light" className="text-body flex-shrink-0">
                            {t(`platformTemplates.category.${item.category}`, {
                              defaultValue: item.category,
                            })}
                          </Badge>
                        </div>
                        {item.variant_label ? (
                          <Badge color="info" className="mt-1">
                            {t("platformTemplates.card.variant", {
                              label: item.variant_label,
                            })}
                          </Badge>
                        ) : null}
                      </CardHeader>
                      <CardBody className="pt-2 d-flex flex-column">
                        <p
                          className="text-muted small flex-grow-1"
                          style={{ minHeight: 40 }}
                        >
                          {description ||
                            t("platformTemplates.card.noDescription")}
                        </p>
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          {(item.tags || []).slice(0, 3).map((slug) => (
                            <Badge
                              key={slug}
                              color="soft-secondary"
                              className="fw-normal"
                            >
                              {t(`platformTemplates.tag.${slug}`, {
                                defaultValue: slug,
                              })}
                            </Badge>
                          ))}
                          {clones > 0 && (
                            <Badge color="soft-primary" className="fw-normal">
                              {t("platformTemplates.card.clones", {
                                count: clones,
                              })}
                            </Badge>
                          )}
                        </div>
                        <div className="d-flex flex-column gap-2">
                          <Button
                            color="primary"
                            disabled={draftBusyId === item.id || cloneBusy}
                            onClick={() => void onQuickDraftCampaign(item)}
                          >
                            {draftBusyId === item.id ? (
                              <Spinner size="sm" />
                            ) : (
                              t("platformTemplates.actions.useDraft")
                            )}
                          </Button>
                          <div className="d-flex gap-2">
                            <Button
                              color="soft-primary"
                              className="flex-grow-1"
                              onClick={() => setPreviewing(item)}
                            >
                              {t("platformTemplates.actions.preview")}
                            </Button>
                            <Button
                              color="light"
                              className="flex-grow-1"
                              onClick={() => setCloning(item)}
                            >
                              {t("platformTemplates.actions.moreOptions")}
                            </Button>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {lastPage > 1 && (
              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mt-4">
                <span className="text-muted small">
                  {t("platformTemplates.pagination.label", {
                    page,
                    lastPage,
                    total,
                  })}
                </span>
                <div className="d-flex gap-2">
                  <Button
                    color="light"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    {t("platformTemplates.pagination.prev")}
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    disabled={page >= lastPage}
                    onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                  >
                    {t("platformTemplates.pagination.next")}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        <Modal
          isOpen={Boolean(previewing)}
          toggle={() => setPreviewing(null)}
          size="xl"
          centered
        >
          <ModalHeader toggle={() => setPreviewing(null)}>
            {previewing?.name}
          </ModalHeader>
          <ModalBody>
            {previewing?.html_body ? (
              <iframe
                title={previewing.name}
                srcDoc={thumbnailSrcDoc(previewing.html_body)}
                sandbox=""
                style={{
                  width: "100%",
                  minHeight: "70vh",
                  border: "1px solid var(--vz-border-color)",
                  borderRadius: 4,
                  background: "#fff",
                }}
              />
            ) : (
              <p className="text-muted mb-0">
                {t("platformTemplates.card.noDescription")}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setPreviewing(null)}>
              {t("platformTemplates.preview.close")}
            </Button>
            <Button
              color="primary"
              disabled={!previewing || draftBusyId === previewing?.id}
              onClick={() => {
                if (previewing) {
                  void onQuickDraftCampaign(previewing);
                }
              }}
            >
              {t("platformTemplates.actions.useDraft")}
            </Button>
            <Button
              color="soft-primary"
              onClick={() => {
                if (previewing) {
                  setCloning(previewing);
                }
              }}
            >
              {t("platformTemplates.actions.moreOptions")}
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={Boolean(cloning)} toggle={() => !cloneBusy && setCloning(null)}>
          <ModalHeader toggle={() => !cloneBusy && setCloning(null)}>
            {t("platformTemplates.clone.title")}
          </ModalHeader>
          <ModalBody>
            <p className="mb-2">
              {t("platformTemplates.clone.description", {
                name: cloning?.name || "",
              })}
            </p>
            <p className="text-muted small mb-0">
              {t("platformTemplates.clone.hint")}
            </p>
            <p className="text-muted small mb-0 mt-2">
              {t("platformTemplates.clone.brandKitHint")}
            </p>
            <p className="text-muted small mb-0 mt-2">
              {t("platformTemplates.clone.testHint")}
            </p>
          </ModalBody>
          <ModalFooter className="flex-wrap gap-2">
            <Button
              color="light"
              disabled={cloneBusy}
              onClick={() => setCloning(null)}
            >
              {t("platformTemplates.clone.cancel")}
            </Button>
            <Button
              color="soft-primary"
              disabled={cloneBusy}
              onClick={() => onClone("template")}
            >
              {cloneBusy ? (
                <Spinner size="sm" />
              ) : (
                t("platformTemplates.clone.asTemplate")
              )}
            </Button>
            <Button
              color="primary"
              disabled={cloneBusy}
              onClick={() => onClone("campaign")}
            >
              {cloneBusy ? (
                <Spinner size="sm" />
              ) : (
                t("platformTemplates.clone.asCampaign")
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default PlatformTemplatesGallery;
